// Powerlete empty collection state (DECISIONS.md D37). Loaded only by
// blocks/_powerlete-empty-collection.liquid, i.e. only on a collection page that holds 0
// products with no filter applied. Three jobs, nothing visual:
//
// 1. Publishes `empty_collection_view` once per render (collection_handle, gender,
//    carousel_source) and `empty_collection_click` for every link/button the visitor uses
//    inside a module (module, item, collection_handle), through assets/powerlete-analytics.js
//    (it waits for Shopify.analytics, which Rocket Loader delays past this module's connect).
//    A click or search that leaves the page is published on the NEXT page (publishOnNextPage):
//    GA4 in the pixel sandbox never sends a hit queued just before navigation. These are
//    CUSTOM events: GA4 only receives them because the "GTM" custom pixel subscribes to them.
// 2. Search submit counts as an `empty_collection_click` on module `search`, item = the query.
// 3. Keeps a polite live region per carousel in step with what is on screen after the
//    visitor scrolls it ("3 to 4 of 8"), so a screen-reader user hears where they are.
//    Never announced on load: the text is only written after a scroll.
//
// The click listener sits on this element, not on document, on purpose: the quick-add "+"
// handler (assets/powerlete-quick-add-sheet.js) calls stopPropagation at document level, so a
// document listener would never see an Add tap.

import { publish, publishOnNextPage } from '@theme/powerlete-analytics';

class PowerleteEmptyCollection extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    this.collectionHandle = this.dataset.collectionHandle || '';

    publish('empty_collection_view', {
      collection_handle: this.collectionHandle,
      gender: this.dataset.gender || 'other',
      carousel_source: this.dataset.carouselSource || '',
    });

    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    this.#bindCarouselStatus();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('submit', this.#onSubmit);
    this.#cleanups.forEach((fn) => fn());
    this.#cleanups = [];
    this.dataset.bound = 'false';
  }

  /** @type {Array<() => void>} */
  #cleanups = [];

  /**
   * True when this click will load another page in this tab (so a live publish would be lost).
   * @param {MouseEvent} event
   * @param {Element} el
   */
  #leavesPage(event, el) {
    if (!(el instanceof HTMLAnchorElement) || event.defaultPrevented) return false;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if ((el.target && el.target !== '_self') || el.hasAttribute('download')) return false;
    const to = new URL(el.href, location.href);
    if (to.origin !== location.origin) return false;
    // A same-page #hash jump stays on this page.
    return !(to.hash && to.pathname === location.pathname && to.search === location.search);
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const el = target?.closest('a[href], button');
    if (!el || !this.contains(el)) return;
    // Carousel arrows move the rail; they are not a choice of item.
    if (el.closest('slideshow-arrows, slideshow-controls')) return;
    if (el.type === 'submit') return; // counted once, on submit
    const moduleEl = el.closest('[data-ecs-module]');
    if (!moduleEl) return;
    const item = el.closest('[data-ecs-item]')?.getAttribute('data-ecs-item') || '';
    const data = {
      module: moduleEl.getAttribute('data-ecs-module'),
      item,
      collection_handle: this.collectionHandle,
    };
    if (this.#leavesPage(event, el)) publishOnNextPage('empty_collection_click', data);
    else publish('empty_collection_click', data);
  };

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const moduleEl = form.closest('[data-ecs-module]');
    if (!moduleEl) return;
    const query = new FormData(form).get('q');
    const data = {
      module: moduleEl.getAttribute('data-ecs-module'),
      item: typeof query === 'string' ? query.trim() : '',
      collection_handle: this.collectionHandle,
    };
    // A plain GET search loads /search; if something intercepted it, the page stays.
    if (event.defaultPrevented) publish('empty_collection_click', data);
    else publishOnNextPage('empty_collection_click', data);
  };

  #bindCarouselStatus() {
    for (const status of this.querySelectorAll('[data-ecs-status]')) {
      const moduleEl = status.closest('[data-ecs-module]');
      const rail = moduleEl?.querySelector('slideshow-slides');
      if (!rail) continue;
      const template = status.getAttribute('data-template') || '';
      let timer = 0;
      const update = () => {
        const box = rail.getBoundingClientRect();
        const slides = Array.from(rail.querySelectorAll(':scope > slideshow-slide'));
        let first = 0;
        let last = 0;
        slides.forEach((slide, i) => {
          const r = slide.getBoundingClientRect();
          // Counted as on screen when at least half of it is inside the rail.
          const visible = Math.min(r.right, box.right) - Math.max(r.left, box.left) >= r.width / 2;
          if (visible) {
            if (!first) first = i + 1;
            last = i + 1;
          }
        });
        if (!first) return;
        status.textContent = template
          .replace('[start]', String(first))
          .replace('[end]', String(last))
          .replace('[count]', String(slides.length));
      };
      const onScroll = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(update, 150);
      };
      rail.addEventListener('scroll', onScroll, { passive: true });
      this.#cleanups.push(() => {
        rail.removeEventListener('scroll', onScroll);
        window.clearTimeout(timer);
      });
    }
  }
}

if (!customElements.get('powerlete-empty-collection')) {
  customElements.define('powerlete-empty-collection', PowerleteEmptyCollection);
}
